import { convertObjectKeysToPascalCase } from './utilities';

/**
 * Get the HGPro base URL based on environment variable or hostname
 * @param {string} hostname - The hostname to check
 * @returns {string} The base URL for HGPro API
 */
export const getHgproBaseurl = (hostname) => {
  if (process.env.HGPRO_BASE_URL) {
    return process.env.HGPRO_BASE_URL;
  }

  // Fall back to hostname-based logic
  switch (true) {
    case hostname.includes('localhost'):
      // TEMPORARY: Using team environment instead of localhost for development
      // The team env doesn't require auth (unlike localhost) which speeds up development
      // TODO: Replace with environment variable to toggle between local/team environments
      // return 'http://localhost:39912'; // Local dev environment
      // return 'https://hgpro-team-gop.healthgrades.zone'; // Team env (no auth required)
      return 'http://localhost:39912';
    case hostname.includes('elevated-profiles-team.healthgrades.zone'):
      return 'https://hgpro-team-gop.healthgrades.zone';
    case hostname.includes('selfservetest'):
      return 'https://update-alpha-testaws.healthgrades.com';
    default:
      return 'https://update.healthgrades.com';
  }
};

/**
 * @param {string} url - URL to fetch
 * @param {string} [apiVersion] - API version to include in the headers
 */
export const get = async (url, apiVersion = '') => {
  return fetch(url, {
    headers: getHeader(apiVersion),
    credentials: 'include',
  }).then(status);
};

/**
 * @param {string} url - URL to post to
 * @param {any} body - Request body
 * @param {string} [apiVersion] - API version to include in the headers
 */
export const post = async (url, body, apiVersion = '') => {
  return fetch(url, {
    method: 'POST',
    headers: getHeader(apiVersion),
    body: JSON.stringify(body),
    credentials: 'include',
  }).then(status);
};

/**
 * Get headers for API requests
 * @param {string} [apiVersion] - API version to include in the headers
 */
const getHeader = (apiVersion = '') => {
  const headers = {
    'Content-Type': 'application/json',
    //"Referrer-Policy": "no-referrer",
    //"Access-Control-Allow-Origin": "*"

    ...(apiVersion ? { 'api-version': apiVersion } : {}),
  };

  return headers;
};

const status = (res) => {
  return new Promise((resolve, reject) => {
    if (res.status === 401) {
      window.location.replace('/');
    } else if (res.status >= 200 && res.status < 300) {
      res
        .json()
        .then((data) => resolve(data))
        .catch((error) => {
          // If JSON parsing fails due to empty response, resolve with null
          // This is common for endpoints like keep-alive that return 200 with no body
          if (
            error.message.includes('Unexpected end of JSON input') ||
            error.message.includes('JSON')
          ) {
            resolve(null);
          } else {
            reject(error);
          }
        });
    } else if (res.status === 500 || res.status === 404) {
      reject({ error: 'Error Processing Request' });
    } else {
      res.json().then((parsedResponse) => {
        reject(parsedResponse);
      });
    }
  });
};

/**
 * Get the HGMD base URL based on environment variable or hostname
 * @returns {string} The base URL for HGMD
 */
export const hgmdBaseurl = () => {
  if (process.env.HGPRO_BASE_URL) {
    return process.env.HGPRO_BASE_URL;
  }

  // Fall back to hostname-based logic
  return window.location.hostname.indexOf('selfservetest') > -1 ||
    window.location.hostname.indexOf('localhost') > -1
    ? 'https://update-alpha-testaws.healthgrades.com/'
    : 'https://update.healthgrades.com/';
};

export const baseurl = () => {
  if (process.env.SELF_SERVE_LAMBDA_BASE_URL) {
    return process.env.SELF_SERVE_LAMBDA_BASE_URL;
  }

  const hostname = window.location.hostname;

  switch (true) {
    case hostname.indexOf('localhost') > -1:
      return 'https://localhost:44339';
    case hostname.indexOf('selfservetest') > -1:
      return 'https://apigwdev.healthgrades.com/selfserve';
    default:
      return 'https://apigw.healthgrades.com/selfserve';
  }
};
const _baseurl = baseurl();

export const storeSession = async (sessionData) => {
  const sessionDataInPascalCase = convertObjectKeysToPascalCase(sessionData);
  try {
    const response = await fetch(`${_baseurl}/SelfService/store-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sessionDataInPascalCase),
    });
    if (!response.ok) {
      console.log(`Session store failed with status ${response.status}`);
    }
    const { sid } = await response.json();
    return sid;
  } catch (error) {
    console.error('Failed to store session:', error);
  }
};

export const UpdateSession = async (sessionId) => {
  try {
    const response = await fetch(
      `${_baseurl}/SelfService/update-session?sid=${sessionId}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      },
    );
    if (!response.ok) {
      console.log(`Request failed with status ${response.status}`);
    }
    const { sid } = await response.json();
    return sid;
  } catch (error) {
    console.error('Failed to update session', error);
  }
};

export const getSession = async (sid, pwid) => {
  try {
    const response = await fetch(
      `${_baseurl}/SelfService/get-session?sid=${sid}`,
    );
    if (!response.ok) {
      console.log(`Request failed with status ${response.status}`);
    }
    const data = await response.json();
    const val = pwid ? await checkSessionStatus(pwid) : false;
    const _data = { ...data, isLoggedIn: val };
    return _data;
  } catch (error) {
    console.error('Failed to retrieve session', error);
  }
};

export async function checkSessionStatus(pwid) {
  const _url = getHgproBaseurl(window.location.hostname);
  const errorResponse = false;
  try {
    const response = await get(
      `${_url}/api/account/validateSessionForSelfServe?pwid=${encodeURIComponent(pwid)}`,
    );
    return response;
  } catch (error) {
    LogErrorMessage(error);
    return errorResponse;
  }
}
export const LogErrorMessage = async (errorMessage) => {
  try {
    const response = await fetch(`${_baseurl}/SelfService/LogUiError`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(errorMessage),
    });
    if (!response.ok) {
      console.log(
        `Logging error to service failed with status code ${response.status}`,
      );
    }
    return response;
  } catch (error) {
    console.error('Failed to log the error:', error);
  }
};
